import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Badge, Button, Card, Input, Modal, ModalFooter } from '@/components/common';
import { getErrorMessage } from '@/services';
import {
  SETTLEMENT_STATUS_LABEL,
  formatPeriod,
  insurerSettlementsService,
  type ReceivedSettlement,
  type SettlementStatus,
} from '@/services/insurerSettlements';
import { formatCurrency, formatDate } from '@/utils';

/**
 * #444: what insurers say they paid this broker, one row per broker-month.
 * Read-only apart from two answers: Acknowledge ("yes, received") and Dispute
 * ("I received a different amount"). Neither changes the amount or touches the
 * ledger; the monthly comparison lives in the statement difference report.
 */

const STATUS_VARIANT: Record<SettlementStatus, 'info' | 'success' | 'warning' | 'default'> = {
  recorded: 'info',
  acknowledged: 'success',
  disputed: 'warning',
  voided: 'default',
};

export function InsurerSettlementsTab() {
  const queryClient = useQueryClient();
  const [disputing, setDisputing] = useState<ReceivedSettlement | null>(null);
  const [received, setReceived] = useState('');
  const [reason, setReason] = useState('');

  const list = useQuery({
    queryKey: ['insurerSettlementsReceived'],
    queryFn: () => insurerSettlementsService.listReceived(),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['insurerSettlementsReceived'] });

  const acknowledge = useMutation({
    mutationFn: (id: string) => insurerSettlementsService.acknowledge(id),
    onSuccess: () => {
      toast.success('Marked as received');
      refresh();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const dispute = useMutation({
    mutationFn: () => insurerSettlementsService.dispute(disputing!.id, received, reason),
    onSuccess: () => {
      toast.success('Dispute sent to the insurer');
      setDisputing(null);
      refresh();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const rows = list.data?.results ?? [];

  const actions = (r: ReceivedSettlement) =>
    r.status === 'voided' ? null : (
      <div className="flex flex-wrap justify-end gap-2">
        {r.status !== 'acknowledged' && (
          <Button size="sm" variant="outline" onClick={() => acknowledge.mutate(r.id)} disabled={acknowledge.isPending}>
            Acknowledge
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setDisputing(r);
            setReceived('');
            setReason('');
          }}
        >
          Dispute
        </Button>
      </div>
    );

  return (
    <Card>
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Insurer settlements</h3>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        Payments your insurers say they made to you, by month. Acknowledge what you received, or dispute an
        amount. This does not change your ledger.
      </p>

      {list.isLoading ? (
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading settlements…</p>
      ) : list.isError ? (
        <p role="alert" className="mt-4 text-sm text-red-600 dark:text-red-400">
          Could not load settlements: {getErrorMessage(list.error)}
        </p>
      ) : rows.length === 0 ? (
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">No insurer has recorded a payment to you yet.</p>
      ) : (
        <>
          {/* Cards under md, table from md up. */}
          <ul className="mt-4 space-y-3 md:hidden" aria-label="Insurer settlements">
            {rows.map((r) => (
              <li key={r.id} className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{r.insurer_name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{formatPeriod(r.period)}</p>
                  </div>
                  <Badge variant={STATUS_VARIANT[r.status]} size="sm">
                    {SETTLEMENT_STATUS_LABEL[r.status]}
                  </Badge>
                </div>
                <p className="mt-2 text-lg font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(r.amount)}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {formatDate(r.paid_on)} · {r.reference}
                </p>
                <div className="mt-2">{actions(r)}</div>
              </li>
            ))}
          </ul>
          <div className="mt-4 hidden overflow-x-auto md:block">
            <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                  <th className="py-2 pr-4">Insurer</th>
                  <th className="py-2 pr-4">For month</th>
                  <th className="py-2 pr-4">Paid on</th>
                  <th className="py-2 pr-4">Reference</th>
                  <th className="py-2 pr-4 text-right">Amount</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="py-2 pr-4">{r.insurer_name}</td>
                    <td className="py-2 pr-4">{formatPeriod(r.period)}</td>
                    <td className="py-2 pr-4 whitespace-nowrap">{formatDate(r.paid_on)}</td>
                    <td className="py-2 pr-4 font-mono text-xs">{r.reference}</td>
                    <td className="py-2 pr-4 text-right">{formatCurrency(r.amount)}</td>
                    <td className="py-2 pr-4">
                      <Badge variant={STATUS_VARIANT[r.status]} size="sm">
                        {SETTLEMENT_STATUS_LABEL[r.status]}
                      </Badge>
                      {r.status === 'disputed' && r.disputed_amount && (
                        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                          you received {formatCurrency(r.disputed_amount)}
                        </span>
                      )}
                    </td>
                    <td className="py-2">{actions(r)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Modal
        isOpen={disputing !== null}
        onClose={() => setDisputing(null)}
        title="Dispute this settlement"
        description={
          disputing
            ? `${disputing.insurer_name} says it paid ${formatCurrency(disputing.amount)} for ${formatPeriod(disputing.period)} (ref ${disputing.reference}).`
            : undefined
        }
      >
        <div className="space-y-3">
          <Input
            label="Amount you actually received (₹)"
            name="received_amount"
            inputMode="decimal"
            value={received}
            onChange={(e) => setReceived(e.target.value)}
            helperText="Enter 0 if nothing arrived."
            required
          />
          <Input
            label="What is wrong"
            name="dispute_reason"
            value={reason}
            maxLength={500}
            onChange={(e) => setReason(e.target.value)}
            required
          />
        </div>
        <ModalFooter>
          <Button variant="outline" onClick={() => setDisputing(null)}>
            Cancel
          </Button>
          <Button onClick={() => dispute.mutate()} isLoading={dispute.isPending} disabled={!received || !reason.trim()}>
            Send dispute
          </Button>
        </ModalFooter>
      </Modal>
    </Card>
  );
}
